import { LocationOnTwoTone } from '@mui/icons-material';
import { Autocomplete, Box, TextField, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';
import objectMerge from 'lodash/merge';
import type { FC, ReactElement } from 'react';
import { useState } from 'react';

import type { AutocompletePrediction } from '@core/types/api/AutocompletePrediction/AutocompletePrediction';
import { LocationResult } from '@core/types/api/LocationResult/LocationResult';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { tenantCountry } from '@core/utils/formikHelpers';
import { DEFAULT_COUNTRY, countryMapView } from '@core/utils/subdivisions';

import { AUTOCOMPLETE_BIAS_RADIUS_METRES } from '../../constants/app';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import { useSlabQueryFetcher } from '../../hooks/useSlabQuery';
import type { QueryRouteBarrelTypes } from '../../utils/ApiClient';
import type { QueryError } from '../../utils/Query';

interface AddressLineProps {
  name: string;
  label: string;
  onAddressChange(address: LocationResult): void;
}

/**
 * Biases predictions toward the tenant's own country.
 *
 * Only applied outside the US: the backend re-sorts predictions by distance
 * whenever a radius is present, so biasing unconditionally would change result
 * ordering for existing US tenants.
 */
const autocompleteBias = (): { lat: string; long: string; radius: string } | undefined => {
  const country = tenantCountry();

  if (country === DEFAULT_COUNTRY) return undefined;

  // Without a country-specific centre there is nothing to bias toward, and the
  // shared default would point a Canadian tenant at the middle of the US.
  const view = countryMapView(country);

  if (view === null) return undefined;

  return {
    lat: String(view.center.lat),
    long: String(view.center.lng),
    radius: String(AUTOCOMPLETE_BIAS_RADIUS_METRES),
  };
};

export const AddressLine: FC<AddressLineProps> = ({ name, label, onAddressChange }) => {
  const theme = useTheme();

  const [field, meta, helper] = useField<string>(name);

  const [options, setOptions] = useState<AutocompletePrediction[]>([]);

  const showError = meta.touched === true && hasValue(meta.error);

  const fieldValue = hasValue(field.value) ? field.value : '';

  const mapFetcher = useSlabQueryFetcher<
    'GET maps autocomplete',
    QueryRouteBarrelTypes['GET maps autocomplete'],
    QueryError
  >();

  const placeFetcher = useSlabQueryFetcher<
    'GET maps place',
    QueryRouteBarrelTypes['GET maps place'],
    QueryError
  >();

  const handleInputChange = useDebouncedCallback(
    async (_: unknown, value: string): Promise<void> => {
      helper.setValue(value);

      if (value === '') return;

      const bias = autocompleteBias();

      mapFetcher('GET maps autocomplete', {
        queryParams: bias === undefined ? { query: value } : { query: value, ...bias },
      })
        .then(setOptions)
        .catch(() => setOptions([]));
    },
    300,
  );

  const handleChange = (_: unknown, place: AutocompletePrediction | null | string): void => {
    if (typeof place !== 'string' && hasValue(place?.place_id)) {
      placeFetcher('GET maps place', { queryParams: { id: place.place_id } })
        .then((location) => {
          // Locality/region predictions resolve without street components, so
          // PickBestAddressFromComponents leaves address.line1 empty. Fall
          // back to the prediction's own text so the picked suggestion stays
          // visible in line 1 and the form's hasStringValue check passes.
          const line1Fallback = place.structured_formatting?.main_text ?? place.description ?? '';
          const line1 = hasStringValue(location.address.line1)
            ? location.address.line1
            : line1Fallback;
          onAddressChange(objectMerge(location, { address: { line1 } }) as LocationResult);
        })
        .catch(() => setOptions([]));
    } else {
      const emptyAddress = objectMerge(LocationResult.zero(), {
        address: { state: '', country: tenantCountry() },
        location: {
          lat: null,
          lng: null,
        },
      });

      onAddressChange(emptyAddress);
      setOptions([]);
    }
  };

  return (
    <Autocomplete
      freeSolo
      autoComplete
      filterSelectedOptions
      value={hasStringValue(field.value) ? field.value : null}
      options={options}
      filterOptions={(opts) => opts}
      onInputChange={handleInputChange}
      onChange={handleChange}
      renderInput={(inputProps) => (
        <TextField
          {...inputProps}
          name={field.name}
          onBlur={field.onBlur}
          fullWidth
          value={fieldValue}
          error={showError}
          helperText={showError ? meta.error : undefined}
          label={label}
        />
      )}
      getOptionLabel={(): string => fieldValue}
      isOptionEqualToValue={(option, val): boolean => {
        if (typeof option === 'string' || typeof val === 'string') {
          return option === val;
        }

        return option?.place_id === val?.place_id;
      }}
      renderOption={(
        { key: _key, ...props },
        option: AutocompletePrediction,
        { index },
      ): ReactElement => {
        const { structured_formatting: formatted } = option;

        return (
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
            component='li'
            {...props}
          >
            <LocationOnTwoTone sx={{ color: theme.palette.grey[600] }} />
            <Box>
              <Typography>{formatted?.main_text}</Typography>
              <Typography variant='body2' color={theme.palette.grey[600]}>
                {formatted?.secondary_text}
              </Typography>
            </Box>
          </Box>
        );
      }}
    />
  );
};
